import ProfileScreen from '@/karehero-app/care/ProfileScreen';
import DocRow from '@/karehero-app/care/DocRow';
import { DocActionsSheet, EditDocSheet } from '@/karehero-app/care/DocSheets';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

const EXAMPLE_DOC = { id: 1, name: 'care_assessment.pdf' };

// The member-side half of Key Documents V0: the care circle reads documents the
// CAM has added and uploads their own from the Docs tab of the Care hub. The
// CAM-facing control-panel side lives in the `documents` handover.
export const careDocumentsSpec: HandoverSpec = {
  id: 'care-documents',
  title: 'Key documents (member app)',
  description:
    'The Docs tab of the Care hub: the care circle reads the documents their care expert has added and uploads their own (ID, bank statements, GP letters) straight from the phone. Each row has a kebab to view, rename or delete. The CAM-side upload and management lives on the control panel.',
  links: [
    {
      label: 'Project: Key Documents V0 (Linear)',
      url: 'https://linear.app/karehero/project/key-documents-v0-initial-setup-29e97808f0d9',
    },
    { label: 'Live flow (Profile › Docs)', url: '/karehero-app/profile' },
    { label: 'CAM-side handover (Key documents)', url: '/handover/documents' },
    { label: 'Member call notes handover', url: '/handover/care-calls' },
  ],
  flows: [
    {
      title: 'Read and upload documents',
      steps: [
        {
          id: 'list',
          title: 'Docs list',
          tap: 'Care › Docs',
          render: () => <ProfileScreen initialTab='docs' />,
        },
        {
          id: 'upload',
          title: 'Upload a document',
          tap: 'Tap ‘Upload document’; the new row spins, then settles',
          render: () => <ProfileScreen initialTab='docs' />,
        },
      ],
    },
    {
      title: 'Manage a document',
      steps: [
        {
          id: 'sheet',
          title: 'Action sheet',
          tap: 'Tap the ⋯ on a document',
          render: () => (
            <DocActionsSheet
              onView={noop}
              onEdit={noop}
              onDelete={noop}
              onClose={noop}
            />
          ),
        },
        {
          id: 'edit',
          title: 'Rename',
          tap: 'Tap ‘Edit’ in the action sheet',
          render: () => (
            <EditDocSheet doc={EXAMPLE_DOC} onSave={noop} onClose={noop} />
          ),
        },
      ],
    },
  ],
  states: [
    {
      id: 'list',
      title: 'Documents list',
      kind: 'happy',
      repro: 'Docs tab with the seed documents',
      render: () => <ProfileScreen initialTab='docs' />,
    },
    {
      id: 'empty',
      title: 'No docs yet',
      kind: 'empty',
      repro: 'emptyDocs prop. First visit, before anything is added',
      render: () => <ProfileScreen initialTab='docs' emptyDocs />,
    },
    {
      id: 'uploading',
      title: 'Uploading row',
      kind: 'edge',
      repro:
        'doc.uploading true: a spinner replaces the kebab while it uploads',
      render: () => (
        <DocRow doc={{ id: 0, name: 'example_file.pdf', uploading: true }} />
      ),
    },
    {
      id: 'actions-sheet',
      title: 'Kebab action sheet',
      kind: 'happy',
      repro: 'Tap the ⋯ on a row: View in browser / Edit / Delete',
      render: () => (
        <DocActionsSheet
          onView={noop}
          onEdit={noop}
          onDelete={noop}
          onClose={noop}
        />
      ),
    },
    {
      id: 'edit-drawer',
      title: 'Rename drawer',
      kind: 'happy',
      repro: 'Action sheet › Edit. Rename the document, save changes',
      render: () => (
        <EditDocSheet doc={EXAMPLE_DOC} onSave={noop} onClose={noop} />
      ),
    },
  ],
  requirements: [
    {
      text: 'Documents list: file icon, name and a kebab menu per row',
      status: 'built',
      where: 'Docs tab',
    },
    {
      text: 'Upload a document from the phone; it appears at the top of the list',
      status: 'built',
      where: 'Docs tab › Upload document',
      note: 'File picker and upload are simulated; the new row spins, then settles',
    },
    {
      text: 'Kebab action sheet: View in browser, Edit, Delete',
      status: 'built',
      where: 'Row ⋯ menu',
    },
    {
      text: 'Rename a document',
      status: 'built',
      where: 'Action sheet › Edit',
    },
    {
      text: 'Delete a document with a confirm',
      status: 'partial',
      where: 'Action sheet › Delete',
      note: 'Delete works; no confirm step on the member side yet (the CAM side confirms)',
    },
    {
      text: 'View / open a document',
      status: 'partial',
      where: 'Action sheet › View in browser',
      note: 'Mocked; closes the sheet. Needs a signed-URL open from storage',
    },
    {
      text: 'Empty state before anything is uploaded',
      status: 'built',
      where: 'No docs yet state',
    },
    {
      text: 'Uploading indicator while a file is in flight',
      status: 'built',
      where: 'Uploading row',
    },
    {
      text: 'Tags, file size and an unread / New badge on rows',
      status: 'not-built',
      note: 'DocRow shows icon + name only; the CAM side carries tags',
    },
    {
      text: 'Notification to the care circle when the CAM adds a document',
      status: 'not-built',
      note: 'Channels are an open question (push / email / in-app)',
    },
    {
      text: 'Documents linked to the action they support',
      status: 'partial',
      note: 'Uploads appear under a call summary; document to action linking is not wired',
    },
    {
      text: 'Document storage & security (GCS, signed URLs, malware scan)',
      status: 'out-of-scope',
      note: 'Backend, out of scope for the design handover',
    },
  ],
  components: {
    mui: [
      'Box',
      'Card',
      'CircularProgress',
      'Stack',
      'TextField',
      'Typography',
      'Button',
    ],
    existing: ['Icon (llama)', 'Button (llama)', 'BottomSheet'],
    new: ['DocRow', 'DocActionsSheet', 'EditDocSheet'],
  },
  componentsNote:
    'DocRow is the list row; DocActionsSheet and EditDocSheet are the kebab action drawer and the rename drawer, both built on the shared BottomSheet. The CAM-side equivalents (DocumentRow, DocumentDialog) live in the Key documents handover.',
  componentPreviews: {
    DocRow: () => <DocRow doc={{ id: 0, name: 'care_assessment.pdf' }} />,
  },
};
